'use client';

import { cn } from '@bells/core/lib/utils';
import {
  crudFormCancel,
  selectNoSelection,
  settingsProfileSubmit,
} from '@bells/i18n';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from '../hooks/use-media-query';
import { Button, type ButtonProps } from './button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from './drawer';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { type Options, SelectionList, type Values } from './selection-list';

export type ResponsiveSelectProps = {
  options: Options | Values;
  value?: string | null | undefined;
  onChange?: (val: string | null) => void;
  disabled?: boolean;
  isOptional?: boolean;
  searchTerm?: string;
  placeholder?: string;
  notFoundLabel?: string;
  title?: string;
  description?: string;
  hasSearch?: boolean;
  ordered?: boolean;
  fullWidth?: boolean;
  isMobileOnlySelection?: boolean;
  isVirtual?: boolean;
  virtualHeight?: number;
} & Omit<
  ButtonProps,
  'onChange' | 'value' | 'variant' | 'children' | 'onSelect'
>;

const ResponsiveSelect = forwardRef<HTMLButtonElement, ResponsiveSelectProps>(
  (
    {
      options,
      value: initValue,
      onChange,
      disabled,
      isOptional = true,
      hasSearch = true,
      className,
      searchTerm,
      placeholder,
      notFoundLabel,
      ordered = true,
      fullWidth = true,
      isMobileOnlySelection = false,
      title,
      description,
      onBlur,
      ...props
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);
    const isDesktop = useMediaQuery('(min-width: 768px)');
    const [value, setValue] = useState<string | null>(initValue ?? null);

    useEffect(() => {
      setValue(initValue ?? null);
    }, [initValue]);

    const activeStatus = useMemo(() => {
      if (!value) {
        return null;
      }

      if (options instanceof Array) {
        return options.find((df) => df.value == value) ?? null;
      } else {
        return options[value] ?? null;
      }
    }, [value, options]);

    const placeholderValue = useMemo(() => {
      return placeholder ?? selectNoSelection();
    }, [placeholder]);

    const [originalData, setOriginalData] = useState<string | null>(
      initValue ?? null
    );

    const restore = () => {
      setOriginalData(initValue ?? null);
    };

    useEffect(() => {
      setOriginalData(initValue ?? null);
    }, [initValue]);

    const closeAndSave = (val: string | null) => {
      setOriginalData(val);
      onChange?.(val);
      setOpen(false);
    };

    if (isMobileOnlySelection) {
      return (
        <SelectionList
          isMobileOnlySelection={true}
          ordered={ordered}
          value={value}
          options={options}
          disabled={disabled}
          notFoundLabel={notFoundLabel}
          searchTerm={searchTerm}
          hasSearch={hasSearch}
          isOptional={isOptional}
          onChange={(val) => {
            setValue(val);
            onChange?.(val);
            setOpen(false);
          }}
        />
      );
    }
    if (isDesktop) {
      return (
        <Popover modal open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              role='combobox'
              type='button'
              ref={ref}
              disabled={disabled}
              onBlur={onBlur}
              variant='outline'
              className={cn(
                'justify-start',
                !activeStatus && 'text-muted-foreground',
                className
              )}
              {...props}
            >
              {activeStatus && typeof activeStatus === 'string' && (
                <span className='line-clamp-1 whitespace-normal text-left'>
                  {activeStatus}
                </span>
              )}
              {activeStatus && typeof activeStatus !== 'string' && (
                <>
                  {activeStatus.icon}
                  <span className='line-clamp-1 whitespace-normal text-left'>
                    {activeStatus.label}
                  </span>
                </>
              )}
              {!activeStatus && (
                <span className='line-clamp-1 whitespace-normal text-left'>
                  {placeholderValue}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            fullWidth={fullWidth}
            className='w-full p-0'
            align='start'
          >
            <SelectionList
              ordered={ordered}
              value={value}
              disabled={disabled}
              notFoundLabel={notFoundLabel}
              searchTerm={searchTerm}
              isOptional={isOptional}
              onChange={(val) => {
                setValue(val);
                onChange?.(val);
                setOriginalData(val);
                setOpen(false);
              }}
              hasSearch={hasSearch}
              options={options}
            />
          </PopoverContent>
        </Popover>
      );
    }

    return (
      <Drawer
        repositionInputs={false}
        open={open}
        onOpenChange={(val) => {
          setOpen(val);
          restore();
        }}
      >
        <DrawerTrigger asChild>
          <Button
            ref={ref}
            role='combobox'
            onBlur={onBlur}
            type='button'
            disabled={disabled}
            variant='outline'
            className={cn(
              'justify-start',
              !activeStatus && 'text-muted-foreground',
              className
            )}
            {...props}
          >
            {activeStatus && typeof activeStatus === 'string' && (
              <span className='line-clamp-1 whitespace-normal break-all text-left'>
                {activeStatus}
              </span>
            )}
            {activeStatus && typeof activeStatus !== 'string' && (
              <>
                {activeStatus.icon}
                <span className='line-clamp-1 whitespace-normal break-all text-left'>
                  {activeStatus.label}
                </span>
              </>
            )}
            {!activeStatus && (
              <span className='line-clamp-1 whitespace-normal break-all text-left'>
                {placeholderValue}
              </span>
            )}
          </Button>
        </DrawerTrigger>

        <DrawerContent>
          {title && (
            <DrawerHeader>
              <DrawerTitle>{title}</DrawerTitle>
              {description && (
                <DrawerDescription>{description}</DrawerDescription>
              )}
            </DrawerHeader>
          )}
          <SelectionList
            ordered={ordered}
            value={originalData}
            options={options}
            disabled={disabled}
            notFoundLabel={notFoundLabel}
            searchTerm={searchTerm}
            hasSearch={hasSearch}
            isOptional={isOptional}
            onChange={(val) => {
              setOriginalData(val);
            }}
          />
          <DrawerFooter className='pt-2'>
            <DrawerClose asChild>
              <div className='flex flex-col'>
                <Button
                  type='button'
                  onClick={() => closeAndSave(originalData ?? null)}
                >
                  {settingsProfileSubmit()}
                </Button>
                <Button
                  onClick={() => {
                    restore();
                    setOpen(false);
                  }}
                  type='button'
                  variant='outline'
                >
                  {crudFormCancel()}
                </Button>
              </div>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }
);

ResponsiveSelect.displayName = 'ResponsiveSelect';
export { ResponsiveSelect };
